import React from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const BillingSummaryOverview = ({
  data,
  loading,
  viewMode,
  setViewMode,
  typeMap,
  subtypeMap,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}) => {
  const filteredData = data.filter((item) => {
    if (!item.sent_at) return false;
    const sent = dayjs(item.sent_at);
    const start = startDate ? dayjs(startDate) : null;
    const end = endDate ? dayjs(endDate) : null;

    return (!start || sent.isSameOrAfter(start, "day")) &&
           (!end || sent.isSameOrBefore(end, "day"));
  });

  const aggregateBillingByDate = () => {
    const grouped = {};
    filteredData.forEach((item) => {
      if (!item.billable || !item.work_order_id || !item.sent_at) return;
      const dateKey = viewMode === "daily"
        ? dayjs(item.sent_at).format("YYYY-MM-DD")
        : dayjs(item.sent_at).format("YYYY-MM");
      grouped[dateKey] = (grouped[dateKey] || 0) + parseFloat(item.price || 0);
    });

    const sortedKeys = Object.keys(grouped).sort();
    return {
      labels: sortedKeys,
      totals: sortedKeys.map((key) => grouped[key]),
    };
  };

  const getBilledCountBySubtype = () => {
    const map = {};
    filteredData.forEach((item) => {
      if (!item.billable || !item.work_order_id) return;
      const subtypeId = item.workorder_subtype_id;
      const subtypeName = (subtypeMap && subtypeMap[subtypeId]) || "Unknown";
      if (!map[subtypeName]) map[subtypeName] = new Set();
      map[subtypeName].add(item.work_order_id);
    });

    const labels = Object.keys(map).sort();
    const values = labels.map((label) => map[label].size);
    return { labels, values };
  };

 const getTotalByItemGroup = () => {
  const map = {};

  filteredData.forEach((item) => {
    if (!item.billable || !item.item_group) return;
    const key = item.item_group || "Unknown";
    map[key] = (map[key] || 0) + parseFloat(item.price || 0);
  });

  const labels = Object.keys(map);
  const values = labels.map((key) => map[key]);
  return { labels, values };
};


  const getTotalByAssociationName = () => {
    const map = {};

    filteredData.forEach((item) => {
      if (!item.billable || !item.association_name) return;
      const name = item.association_name;
      map[name] = (map[name] || 0) + parseFloat(item.price || 0);
    });

    const labels = Object.keys(map);
    const values = labels.map((name) => map[name]);
    return { labels, values };
  };

  const { labels, totals } = aggregateBillingByDate();
  const { labels: subtypeLabels, values: subtypeCounts } = getBilledCountBySubtype();
  const { labels: itemGroupLabels, values: itemGroupValues } = getTotalByItemGroup();
  const { labels: assocLabels, values: assocValues } = getTotalByAssociationName();

  const billingChartData = {
    labels,
    datasets: [{
      label: `Total Billing (${viewMode})`,
      data: totals,
      backgroundColor: "#85C1E9",
      borderColor: "#2980B9",
      borderWidth: 1,
    }],
  };

  const subtypeChartData = {
    labels: subtypeLabels,
    datasets: [{
      label: "Work Orders Billed",
      data: subtypeCounts,
      backgroundColor: "#F5B041",
      borderColor: "#B9770E",
      borderWidth: 1,
    }],
  };

  const assocChartData = {
    labels: assocLabels,
    datasets: [{
      label: "Total Billing per Association",
      data: assocValues,
      backgroundColor: "#BB8FCE",
      borderColor: "#884EA0",
      borderWidth: 1,
    }]
  };

  const itemGroupChartData = {
    labels: itemGroupLabels,
    datasets: [{
      label: "Total Billing per Item Group",
      data: itemGroupValues,
      backgroundColor: "#58D68D",
      borderColor: "#1D8348",
      borderWidth: 1,
    }]
  };

  const billingChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label: (ctx) => `$${ctx.parsed.y.toFixed(2)}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => `$${v}` }
      }
    }
  };

  const subtypeChartOptions = {
    indexAxis: "y",
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.parsed.x} work orders`
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          stepSize: 1,
        }
      }
    }
  };

  const horizontalMoneyOptions = {
    indexAxis: "y",
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `$${ctx.parsed.x.toFixed(2)}`
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { callback: (v) => `$${v}` }
      }
    }
  };

  return (
    <>
      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate || ""}
          onChange={(e) => setStartDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate || ""}
          onChange={(e) => setEndDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Total Billing Amount ({viewMode})
            </Typography>
            <Box sx={{ minHeight: 400 }}>
              {loading
                ? <Typography>Loading...</Typography>
                : <Bar data={billingChartData} options={billingChartOptions} />}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Work Orders Billed by Subtype
            </Typography>
            <Box sx={{ minHeight: 400 }}>
              {loading
                ? <Typography>Loading...</Typography>
                : <Bar data={subtypeChartData} options={subtypeChartOptions} />}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Total Billing per Association
            </Typography>
            <Box sx={{ minHeight: 400 }}>
              {loading
                ? <Typography>Loading...</Typography>
                : <Bar data={assocChartData} options={horizontalMoneyOptions} />}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Total Billing per Item Group
            </Typography>

            <Box sx={{ minHeight: 400 }}>
              {loading
                ? <Typography>Loading...</Typography>
                : <Bar data={itemGroupChartData} options={horizontalMoneyOptions} />}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default BillingSummaryOverview;
